import React, { useId } from "react";
import { FiStar, FiRefreshCw, FiRotateCcw, FiMinus, FiPlus, FiX } from "react-icons/fi";
import useAIDescription, { DEFAULT_MAX_LENGTH } from "../../../hooks/useAIDescription";
import "./AIDescriptionAssist.css";

const TONES = ["Professional", "Friendly", "Luxury", "Short"];
const LANGUAGES = ["English", "Hindi"];

/**
 * Wraps a description field with a "Generate with AI" toolbar (one component for every form).
 *
 * <AIDescriptionAssist type="property" values={formValues} value={text} onChange={setText} maxLength={2000} format="html">
 *   ...the existing textarea / editor, unchanged...
 * </AIDescriptionAssist>
 *
 * `type` selects the field map in utils/aiDescription.js. `format="html"` is for rich-text editors.
 * The wrapped field stays fully editable; nothing is submitted by this component.
 */
const AIDescriptionAssist = ({
  type,
  values,
  value,
  onChange,
  maxLength = DEFAULT_MAX_LENGTH,
  format = "plain",
  showCounter = false,
  disabled = false,
  children,
}) => {
  const uid = useId();
  const ai = useAIDescription({ type, values, value, onChange, maxLength, format });
  const loading = ai.status === "loading";
  const blocked = disabled || ai.missing.length > 0;
  const hintId = `${uid}-hint`;
  const length = String(value ?? "").length;

  return (
    <div className="ai-assist" aria-busy={loading}>
      <div className="ai-assist__toolbar">
        <div className="ai-assist__options">
          <label className="ai-assist__option">
            <span className="visually-hidden">Tone</span>
            <select value={ai.tone} onChange={(e) => ai.setTone(e.target.value)} disabled={loading} aria-label="Tone">
              {TONES.map((tone) => (
                <option key={tone} value={tone}>
                  {tone}
                </option>
              ))}
            </select>
          </label>
          <label className="ai-assist__option">
            <span className="visually-hidden">Language</span>
            <select
              value={ai.language}
              onChange={(e) => ai.setLanguage(e.target.value)}
              disabled={loading}
              aria-label="Language"
            >
              {LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <button type="button" className="ai-assist__btn ai-assist__btn--cancel" onClick={ai.cancel}>
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            <span>Writing… Cancel</span>
          </button>
        ) : (
          <button
            type="button"
            className="ai-assist__btn"
            onClick={ai.generate}
            disabled={blocked}
            aria-describedby={ai.missing.length ? hintId : undefined}
            title={ai.missing.length ? ai.missingMessage : "Generate a description from the details you've filled in"}
          >
            <FiStar size={14} aria-hidden="true" />
            <span>Generate with AI</span>
          </button>
        )}
      </div>

      {ai.missing.length > 0 && (
        <p id={hintId} className="ai-assist__hint">
          {ai.missingMessage}
        </p>
      )}

      <div className="ai-assist__status" role="status" aria-live="polite">
        {loading && <span className="ai-assist__writing">Writing your description…</span>}
      </div>

      {ai.confirming && (
        <div className="ai-assist__confirm" role="alertdialog" aria-label="Replace your current text?">
          <span>Replace your current text?</span>
          <div className="ai-assist__confirm-actions">
            <button type="button" className="ai-assist__link ai-assist__link--danger" onClick={ai.replaceWithNew}>
              Replace
            </button>
            <button type="button" className="ai-assist__link" onClick={ai.improveExisting}>
              Improve my text
            </button>
            <button type="button" className="ai-assist__link" onClick={ai.dismissConfirm}>
              <FiX size={12} aria-hidden="true" /> Keep mine
            </button>
          </div>
        </div>
      )}

      <div className={`ai-assist__field${loading ? " ai-assist__field--loading" : ""}`}>{children}</div>

      {showCounter && (
        <div className={`ai-assist__counter${length > maxLength ? " ai-assist__counter--over" : ""}`}>
          {length}/{maxLength} characters
        </div>
      )}

      {ai.error && (
        <div className="ai-assist__error" role="alert">
          <span>{ai.error.message}</span>
          <button type="button" className="ai-assist__link" onClick={ai.retry}>
            Retry
          </button>
        </div>
      )}

      {ai.hasGenerated && !loading && (
        <div className="ai-assist__actions">
          <button type="button" className="ai-assist__link" onClick={ai.regenerate} disabled={blocked}>
            <FiRefreshCw size={12} aria-hidden="true" /> Regenerate
          </button>
          <button type="button" className="ai-assist__link" onClick={ai.shorter} disabled={blocked}>
            <FiMinus size={12} aria-hidden="true" /> Shorter
          </button>
          <button type="button" className="ai-assist__link" onClick={ai.longer} disabled={blocked}>
            <FiPlus size={12} aria-hidden="true" /> Longer
          </button>
          <button type="button" className="ai-assist__link" onClick={ai.undo} disabled={!ai.canUndo}>
            <FiRotateCcw size={12} aria-hidden="true" /> Undo
          </button>
        </div>
      )}

      <p className="ai-assist__note">AI-generated. Please review before posting.</p>
    </div>
  );
};

export default AIDescriptionAssist;
